import type { SupabaseClient } from '@supabase/supabase-js';
import { openModal, showToast } from '@vario-connect/frontend-design';
import { escapeHtml } from './otto-list-mapping.js';
import {
  completeOfferAssignment,
  loadAssignModes,
  loadOfferChoices,
  type CategoryMode,
} from './otto-offers-article-dialog.js';

/**
 * „Markierte Vario-Artikel zuordnen“: übernimmt für mehrere markierte Angebote den Vorschlag aus
 * dem VARIO-Abgleich (vario.article_match_suggestions, Filter „In Vario vorhanden, nicht
 * zugeordnet“). Je Angebot derselbe Ablauf wie die Einzel-Zuordnung: otto_assign_article, dann
 * completeOfferAssignment (Kanalpreis, Verkaufskanal samt Lister-Zeile, Kategorie, Marke).
 * Läuft nacheinander, damit VARIO nicht mit parallelen Kanal-Anlagen überrollt wird.
 */

/** Was der Sammel-Dialog von einer Grid-Zeile braucht. */
export interface BulkAssignRow {
  sku: string;
  ean: string | null;
  price_cents: number | null;
  vario_article_id: string | null;
  suggested_vario_article_id: string | null;
  suggested_vario_article_number: string | null;
}

/** Zuordenbar = noch nicht zugeordnet und mit Vorschlag. Rein (kein I/O). */
export function pickAssignable<T extends BulkAssignRow>(rows: ReadonlyArray<T>): T[] {
  return rows.filter((r) => r.vario_article_id == null && r.suggested_vario_article_id != null);
}

/** Übernehmen ja/nein aus Einstellung und (bei „fragen“) der Checkbox im Sammel-Dialog. */
export function decide(mode: CategoryMode, askChecked: boolean): boolean {
  return mode === 'take' || (mode === 'ask' && askChecked);
}

interface BulkCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

const MODE_HINT: Record<CategoryMode, string> = {
  take: 'wird laut Einstellung übernommen',
  skip: 'wird laut Einstellung nicht übernommen',
  ask: '',
};

export function openBulkAssignModal(ctx: BulkCtx, rows: BulkAssignRow[], onDone: () => void): void {
  const assignable = pickAssignable(rows);
  const skipped = rows.length - assignable.length;
  const listHtml = assignable.slice(0, 50).map((r) =>
    `<tr><td class="text-mono">${escapeHtml(r.sku)}</td><td class="text-mono">${escapeHtml(r.suggested_vario_article_number ?? r.suggested_vario_article_id)}</td></tr>`,
  ).join('');
  const more = assignable.length > 50 ? `<div class="kl-form-hint">… und ${assignable.length - 50} weitere</div>` : '';

  const body = `
    <div class="kl-modal-meta">
      <strong>${assignable.length} ${assignable.length === 1 ? 'Angebot' : 'Angebote'} zuordnen</strong>
      <div class="text-grey">${skipped > 0 ? `${skipped} markierte ohne Vorschlag oder schon zugeordnet (werden übersprungen)` : 'Je Angebot der vorgeschlagene VARIO-Artikel'}</div>
    </div>
    ${assignable.length ? `<div style="max-height:220px;overflow:auto;margin-bottom:12px;">
      <table class="kl-restable"><thead><tr><th>OTTO-SKU</th><th>VARIO-Artikel</th></tr></thead><tbody>${listHtml}</tbody></table>${more}
    </div>` : ''}
    <div id="kl-ba-opts"><div class="kl-form-hint">Lade Einstellungen…</div></div>
    <div class="kl-form-hint">Wie bei der Einzel-Zuordnung wird je Artikel der OTTO-Verkaufskanal angelegt und der OTTO-Preis übernommen.</div>
    <div class="kl-form-hint" id="kl-ba-progress" hidden></div>
    <div class="kl-modal-error" id="kl-ba-error" hidden></div>
  `;
  const footer = `
    <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
    <button type="button" class="v-button v-button--primary" data-action="apply" disabled>
      <span class="v-icon fa-regular fa-link"></span>Zuordnen
    </button>
  `;
  const modal = openModal({ title: 'Markierte Vario-Artikel zuordnen', icon: 'fa-link', width: 560, body, footer });
  const applyBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="apply"]')!;
  const optsEl = modal.el.querySelector<HTMLDivElement>('#kl-ba-opts')!;
  const progressEl = modal.el.querySelector<HTMLDivElement>('#kl-ba-progress')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#kl-ba-error')!;

  if (assignable.length === 0) {
    optsEl.innerHTML = '';
    errEl.textContent = 'Keines der markierten Angebote hat einen Vorschlag. Im Filter „In Vario vorhanden, nicht zugeordnet“ stehen die passenden.';
    errEl.hidden = false;
    return;
  }

  let modes: { category: CategoryMode; brand: CategoryMode } = { category: 'skip', brand: 'skip' };
  const optRow = (id: string, label: string, mode: CategoryMode) => mode === 'ask'
    ? `<label class="kl-check-row" style="display:flex;align-items:center;gap:10px;padding:4px 2px;cursor:pointer;">
         <input type="checkbox" id="${id}" checked><span style="flex:1;">${label} übernehmen, falls noch keine gesetzt ist</span></label>`
    : `<div class="kl-form-hint">${label}: ${MODE_HINT[mode]}.</div>`;
  void loadAssignModes(ctx.supabase)
    .then((m) => { modes = m; })
    .catch(() => { /* ohne Einstellung wird nichts übernommen */ })
    .finally(() => {
      optsEl.innerHTML = optRow('kl-ba-cat', 'OTTO-Kategorie', modes.category) + optRow('kl-ba-brand', 'OTTO-Marke', modes.brand);
      applyBtn.disabled = false;
    });

  applyBtn.addEventListener('click', async () => {
    applyBtn.disabled = true;
    applyBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>Ordne zu …';
    const takeCategory = decide(modes.category, !!modal.el.querySelector<HTMLInputElement>('#kl-ba-cat')?.checked);
    const takeBrand = decide(modes.brand, !!modal.el.querySelector<HTMLInputElement>('#kl-ba-brand')?.checked);
    progressEl.hidden = false;

    let ok = 0;
    const warned: string[] = [];
    const failed: string[] = [];
    for (let i = 0; i < assignable.length; i++) {
      const r = assignable[i]!;
      progressEl.textContent = `${i + 1} von ${assignable.length}: ${r.sku}`;
      try {
        const { error } = await ctx.supabase.rpc('otto_assign_article', {
          _sku: r.sku,
          _vario_article_id: r.suggested_vario_article_id,
          _vario_article_number: r.suggested_vario_article_number,
          _vario_article_name: null,
        });
        if (error) throw new Error(error.message);
        const choices = takeCategory || takeBrand ? await loadOfferChoices(ctx.supabase, r.sku) : { category: null, brand: null };
        const done = await completeOfferAssignment(
          { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, token: ctx.session.token },
          { sku: r.sku, ean: r.ean, priceCents: r.price_cents },
          { category: takeCategory ? choices.category : null, brand: takeBrand ? choices.brand : null },
        );
        ok += 1;
        const issues = [done.warn, ...done.errors].filter((x): x is string => !!x);
        if (issues.length) warned.push(`${r.sku}: ${issues.join(', ')}`);
      } catch (e) {
        failed.push(`${r.sku}: ${(e as Error).message}`);
      }
    }

    onDone();
    if (failed.length || warned.length) {
      // Dialog offen lassen, damit die betroffenen SKUs lesbar bleiben.
      const list = (title: string, items: string[]) => items.length
        ? `<strong>${title}</strong><ul>${items.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul>` : '';
      errEl.innerHTML = list(`${failed.length} nicht zugeordnet`, failed) + list(`${warned.length} zugeordnet, aber unvollständig`, warned);
      errEl.hidden = false;
      progressEl.textContent = `${ok} von ${assignable.length} zugeordnet.`;
      applyBtn.hidden = true;
      const cancel = modal.el.querySelector<HTMLButtonElement>('[data-modal-cancel]');
      if (cancel) cancel.textContent = 'Schließen';
      return;
    }
    showToast(`${ok} ${ok === 1 ? 'Angebot' : 'Angebote'} zugeordnet, Verkaufskanal am Artikel angelegt.`, 'success');
    modal.close();
  });
}
